'use client';

import { type ReactNode, useState } from 'react';

import {
  OptionsButton,
  Popover,
  SecondaryButton,
  Tooltip,
} from '../atomic-atoms';

type OptionsMenuAction = {
  key: string;
  label: string;
  onSelect: (anchor?: HTMLElement | null) => void;
  icon?: ReactNode;
  disabled?: boolean;
  disabledText?: string;
  tone?: 'default' | 'danger';
  loading?: boolean;
};

type OptionsMenuProps = {
  ariaLabel: string;
  actions: OptionsMenuAction[];
  onOpen?: (anchor: HTMLElement) => void;
  onClose?: () => void;
};

export function OptionsMenu({
  ariaLabel,
  actions,
  onOpen,
  onClose,
}: OptionsMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    const anchor = e.currentTarget;
    setAnchorEl(anchor);
    onOpen?.(anchor);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onClose?.();
  };

  return (
    <>
      <OptionsButton ariaLabel={ariaLabel} onClick={(e) => handleOpen(e)} />

      <Popover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          role="menu"
        >
          {actions.map((action) => {
            const isDisabled = Boolean(action.disabled || action.loading);
            const button = (
              <SecondaryButton
                key={action.key}
                ariaLabel={action.label}
                disabled={isDisabled}
                onClick={() => {
                  if (isDisabled) {
                    return;
                  }

                  action.onSelect(anchorEl);
                  handleClose();
                }}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  letterSpacing: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 8,
                  ...(action.tone === 'danger'
                    ? {
                        borderColor: 'var(--nn-accent-red)',
                        color: 'var(--nn-accent-red)',
                      }
                    : null),
                  ...(isDisabled
                    ? {
                        opacity: 0.5,
                        cursor: 'not-allowed',
                      }
                    : null),
                }}
              >
                {action.icon ?? ''}
                <span>{action.label}</span>
              </SecondaryButton>
            );

            if (isDisabled && action.disabledText) {
              return (
                <Tooltip
                  key={action.key}
                  title={action.disabledText}
                  wrapperStyle={{
                    width: '100%',
                  }}
                >
                  {button}
                </Tooltip>
              );
            }

            return button;
          })}
        </div>
      </Popover>
    </>
  );
}
